import { defineStore } from 'pinia';
import { authService, Credentials, User } from '@/services/authService';
import { useApiError } from '@/composables/useApiError';

/** Role required to access the authenticated UI (login and private routes). */
export const UI_ACCESS_ROLE = 'ROLE_ADMIN';

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: authService.getCurrentUser(),
    token: localStorage.getItem('token'),
    loading: false,
    error: null,
  }),
  getters: {
    isAuthenticated: (state): boolean => !!state.token,
    userRole: (state): string => state.user?.roles?.[0] || 'GUEST',
    /** True if the current user has ROLE_ADMIN (and thus can use the authenticated UI). */
    hasUiAccess: (state): boolean => {
      const roles = state.user?.roles;
      return Array.isArray(roles) && roles.includes(UI_ACCESS_ROLE);
    },
  },
  actions: {
    async login(credentials: Credentials) {
      this.loading = true;
      this.error = null;
      try {
        const response = await authService.login(credentials);
        // Extract user info from top-level fields in response.data
        const {
          token,
          id,
          username,
          email,
          phone,
          firstName,
          lastName,
          dob,
          gender,
          address,
          roles,
        } = response.data;
        const user = {
          id,
          username,
          email,
          phone,
          firstName,
          lastName,
          dob,
          gender,
          address,
          roles,
        };
        this.token = token;
        this.user = user;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));

        console.log('Auth store login completed:', {
          token: !!this.token,
          user: !!this.user,
          isAuthenticated: this.isAuthenticated,
        });

        // Note: Success notification will be handled by the component calling this action

        return user;
      } catch (error) {
        const { handleApiError } = useApiError();
        handleApiError(error, 'login');
        throw error;
      } finally {
        this.loading = false;
      }
    },
    logout() {
      console.log('Auth store logout method called');
      authService.logout();
      this.user = null;
      this.token = null;
      console.log('User and token cleared from store');

      // Note: Logout notification will be handled by the component calling this action
    },
  },
});
